import { useState, type Dispatch, type SetStateAction } from "react";
import {
  HERO_BASE_PRESETS,
  HERO_CATALOG,
  allPartIds,
  customHeroIds,
  HERO_STILL_VIEWS,
  heroStillKey,
  nextPartId,
  LOOK_OVERLAY_CATEGORIES,
  PART_CATEGORIES,
  heroBaseKey,
  heroPartKey,
  type OverlayCategory,
  heroPartImageless,
  type PartCategory,
  uiArtKeys,
  uiArtKey,
  UI_ART,
  UI_ART_GROUP_LABEL,
  type UiArtGroup,
  reorderDepth,
  singleSlotKey,
  type AssetSlot,
  type ZOrderOp,
  overlayDirReport,
  overlayDirKeys,
  type OverlayDir,
} from "@greg-chick/shared";
import {
  type BookPageRef,
  type Manifest,
  type ManifestEntry,
} from "./lib/contentApi";
import { isShownOnWeb } from "./lib/effectiveArt";
import { SlotUploader } from "./components/SlotUploader";
import { Fold } from "./components/Fold";
import { StripBuilder } from "./components/StripBuilder";
import { useRegisterSave } from "./lib/saveBar";

/**
 * 🖼️ 그림 전체 목록 — 올릴 수 있는 자리를 분류별로 전부 모아 보여준다(도구·고급).
 *
 * App.tsx 안에 있던 400줄을 떼어냈다. 라우팅 파일이 화면 하나를 통째로 품고 있으면
 * "어디를 고쳐야 하지"가 파일 이름으로 드러나지 않는다(docs/build/20 B5).
 * 여기서만 쓰는 로스터·헬퍼도 함께 옮겼다 — App.tsx 는 어느 것도 쓰지 않는다.
 *
 * 일상 작업은 [🏞️ 체험존 그림]·[🧸 캐릭터]가 맡고, 여기는 "전부 다 보고 싶을 때" 오는 곳이다.
 */

const WORLD_ZONES = ["lobby", "gyeongju", "book", "goods", "event"];
const WORLD_SLOT_KEYS = ["world.background", "world.tileset", "world.wall"];
// 캐릭터 로스터(확정 명명): 그레그=흰 말랑 블롭, 검은 고양이, 병아리 — 웹 표기와 일치시킬 것.
const NPC_ROSTER = [
  { id: "greg_white", label: "그레그(흰 블롭)" },
  { id: "greg_cat", label: "검은 고양이" },
  { id: "chick", label: "병아리" },
];
const CHAR_DIRS = [
  { d: "down", label: "앞(정면)" },
  { d: "up", label: "뒤" },
  { d: "side", label: "옆" },
];
// "머리(head)"·"표정(face)"은 없앴다 — 아이 그림에 이미 얼굴·머리카락이 그려져 있다(shared/hero.ts 주석 참고).
const HERO_CAT_LABEL: Record<string, string> = {
  body: "아이", hair: "모자·머리장식", outfit: "옷", acc: "소품",
};
// 메인 화면 무대 캐릭터 슬롯 — 위치 기준 명명(그림은 교체 가능: 현재 왼쪽=책읽는 그레그).
const LANDING_CHARS = [
  { id: "greg", label: "메인 왼쪽 캐릭터" },
  { id: "chick", label: "메인 오른쪽 캐릭터(병아리)" },
];
const DECOR_PRESET = [
  { id: "hanok", label: "한옥" }, { id: "observatory", label: "첨성대" },
  { id: "bookshelf", label: "책장" }, { id: "stall", label: "굿즈 가판" },
  { id: "stage", label: "무대" }, { id: "tree", label: "나무" },
  { id: "rock", label: "바위" }, { id: "sign", label: "표지판" },
  { id: "lantern", label: "청사초롱" }, { id: "lotus", label: "연꽃" }, { id: "stonelamp", label: "석등" },
];

interface Item {
  id: string; // 안전 식별자(매니페스트 키·맵 참조용)
  label: string; // 표시 이름(한글 가능)
}
/** 한글 등 입력 제목 → 안전 id. 영숫자가 있으면 그걸 쓰고, 없으면(한글만) 안정적 짧은 해시. */
function makeId(title: string): string {
  const ascii = title.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 24);
  if (ascii) return ascii;
  let h = 0;
  for (let i = 0; i < title.length; i++) h = (h * 31 + title.charCodeAt(i)) >>> 0;
  return "c" + h.toString(36);
}
/** id 기준 병합(나중 라벨이 우선, 첫 등장 순서 유지). */
function mergeItems(...lists: Item[][]): Item[] {
  const m = new Map<string, string>();
  for (const list of lists) for (const it of list) if (it.id) m.set(it.id, it.label || m.get(it.id) || it.id);
  return [...m].map(([id, label]) => ({ id, label }));
}
/** 매니페스트에서 prefix 로 시작하는 커스텀 항목(미리 정의 외) — 라벨은 대표키의 alt 에서 복원. */
function manifestCustomItems(
  assets: Record<string, { alt?: string } | undefined>,
  prefix: string,
  predefined: string[],
  repKey: (id: string) => string,
): Item[] {
  const re = new RegExp(`^${prefix.replace(/\./g, "\\.")}\\.([a-z0-9_]+)`);
  const ids: string[] = [];
  for (const k of Object.keys(assets)) {
    const m = re.exec(k);
    if (m && m[1] && !predefined.includes(m[1]) && !ids.includes(m[1])) ids.push(m[1]);
  }
  return ids.map((id) => ({ id, label: assets[repKey(id)]?.alt || id }));
}
// "몇 개가 채워졌나" — **지금 화면에 무언가 나오는가** 기준이다(올린 것·번들·이모지).
// 매니페스트만 세면, 번들 그림으로 멀쩡히 나오는 UI 가 0/24 로 보여 "아무것도 없다"고 오해한다.
const DIR_LABEL: Record<OverlayDir, string> = { down: "앞", side: "옆", up: "뒤" };

const countFilled = (assets: Record<string, unknown>, keys: string[]): [number, number] => [
  keys.filter((k) => isShownOnWeb(k, !!assets[k])).length,
  keys.length,
];

/** 새 항목 추가 입력줄. 제목은 한글 가능(라벨), id 는 자동 생성. */
function AddBar({ onAdd, placeholder }: { onAdd: (title: string) => void; placeholder: string }) {
  const [v, setV] = useState("");
  return (
    <form
      className="addbar"
      onSubmit={(e) => {
        e.preventDefault();
        const t = v.trim();
        if (t) {
          onAdd(t);
          setV("");
        }
      }}
    >
      <input value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder} maxLength={24} />
      <button type="submit" className="btn">＋ 항목 추가</button>
    </form>
  );
}

export function AssetsTab({
  err,
  slotByKey,
  pages,
  manifest,
  setEntry,
  singleSlots,
  dirtyCount,
  onPublish,
  onExport,
  only,
  draft,
}: {
  err: string | null;
  slots: AssetSlot[];
  slotByKey: Record<string, AssetSlot>;
  /** 🔴 `null` 은 **못 읽었다** — 「0쪽」과 다른 말을 해야 한다. */
  pages: BookPageRef[] | null;
  manifest: Manifest;
  setEntry: (key: string, entry: ManifestEntry | null) => void;
  singleSlots: AssetSlot[];
  dirtyCount: number;
  onPublish: () => Promise<void>;
  onExport: () => void;
  /** 한 분류만 보여주는 모드(예: 홈 화면 탭이 "메인 화면"만 끼워 쓴다). 좌측 분류 네비를 숨긴다. */
  only?: string;
  /** 💾 하던 일만 저장 — **바꾼 키를 아는 쪽**(App)이 만들어 넘긴다. */
  draft?: { data: () => unknown; restore: (d: unknown) => void };
}) {
  const a = manifest.assets as Record<string, ManifestEntry>;
  // 게시 버튼은 화면 아래 고정 바 하나로. 예전엔 여기와 [체험존 그림]에 **같은 매니페스트**를
  // 올리는 버튼이 둘이라 "어느 걸 눌러야 하지"가 됐다.
  useRegisterSave({ id: "assets", label: "그림 전체", dirty: dirtyCount, save: onPublish, draft });
  // 그림 넣기 안의 카테고리도 새로고침 유지(미지값이면 activeCat 이 첫 카테고리로 폴백)
  const [cat, setCatState] = useState(() => localStorage.getItem("gc_admin_assets_cat") ?? "");
  const setCat = (c: string) => {
    setCatState(c);
    localStorage.setItem("gc_admin_assets_cat", c);
  };
  const [extraNpc, setExtraNpc] = useState<Item[]>([]);
  const [extraLanding, setExtraLanding] = useState<Item[]>([]);
  const [extraDecor, setExtraDecor] = useState<Item[]>([]);
  /**
   * 아직 그림을 안 올린 **새 모자·소품 자리**.
   *
   * 목록은 매니페스트가 정하므로(allPartIds) 그림을 올리기 전까지는 어디에도 안 나온다.
   * 「＋ 새로 만들기」를 누른 뒤 올릴 칸이 보여야 하니 그 사이를 이 상태가 잇는다.
   * 저장하면 매니페스트에 들어가고 이 값은 필요 없어진다(새로고침하면 사라져도 맞다).
   */
  const [extraParts, setExtraParts] = useState<{ cat: PartCategory; id: string }[]>([]);

  // 커스텀 항목 제거: 해당 키들의 이미지(매니페스트 엔트리) 삭제 + 목록에서 제거
  const removeItem = (keys: string[], item: Item, setExtra: Dispatch<SetStateAction<Item[]>>) => {
    if (!window.confirm(`'${item.label}' 항목을 제거할까요? 업로드한 이미지도 함께 지워집니다.`)) return;
    keys.forEach((k) => setEntry(k, null));
    setExtra((prev) => prev.filter((x) => x.id !== item.id));
  };
  // 항목 추가(제목 한글 가능 → 라벨, id 자동) — 중복 id 는 무시
  const addItem = (title: string, setExtra: Dispatch<SetStateAction<Item[]>>) => {
    const id = makeId(title);
    setExtra((prev) => (prev.some((x) => x.id === id) ? prev : [...prev, { id, label: title }]));
  };

  // 캐릭터(플레이어 + NPC 로스터 + 커스텀), 메인캐릭터, 장식 — 모두 항목 추가 가능(라벨 한글)
  const rosterIds = NPC_ROSTER.map((n) => n.id);
  const npcItems = mergeItems(NPC_ROSTER, manifestCustomItems(a, "npc", rosterIds, (id) => `npc.${id}.down`), extraNpc);
  const characters = [
    { key: "avatar.player", id: "avatar.player", label: "플레이어(나의 캐릭터)", custom: false },
    ...npcItems.map((it) => ({ key: `npc.${it.id}`, id: it.id, label: `NPC · ${it.label}`, custom: !rosterIds.includes(it.id) })),
  ];
  const landingItems = mergeItems(LANDING_CHARS, manifestCustomItems(a, "landing.char", LANDING_CHARS.map((c) => c.id), (id) => `landing.char.${id}`), extraLanding);
  const decorItems = mergeItems(DECOR_PRESET, manifestCustomItems(a, "decor", DECOR_PRESET.map((d) => d.id), (id) => `decor.${id}`), extraDecor);

  const charKeys = characters.flatMap((c) => CHAR_DIRS.map((d) => `${c.key}.${d.d}`));
  // 완성형 탐험가 프리셋: 정지 이미지(hero.base.<id>) + 방향 걷기 스트립(.down/.side/.up). hero.base 슬롯이 있을 때만.
  const showPresets = !!slotByKey["hero.base"];
  /**
   * 🔴 **한 목록으로 만든다.** 예전엔 세는 쪽(`presetKeys`)은 운영자가 만든 캐릭터까지
   * 넣었는데 **그리는 쪽은 기본 다섯만** 돌았다. 그래서 「그림 전체 목록」에 전체가 없었다 —
   * 숫자는 맞고 화면만 비어서, 아무 에러 없이 조용히 어긋났다.
   *
   * 걷는 그림(앞·옆·뒷모습)도 여기 들어간다. 「전체 목록」이 전체가 아니면 이름이 거짓말이다.
   */
  const heroRows = showPresets
    ? [...HERO_BASE_PRESETS.map((p) => p.id), ...customHeroIds(Object.keys(a))].map((id) => ({
        id,
        label: a[heroBaseKey(id)]?.alt || HERO_BASE_PRESETS.find((p) => p.id === id)?.label || id,
        walk: CHAR_DIRS.map(({ d, label }) => ({ key: `${heroBaseKey(id)}.${d}`, label: `걷기 · ${label}` })),
        stills: HERO_STILL_VIEWS.map((v) => ({ key: heroStillKey(heroBaseKey(id), v.sfx), label: v.label })),
      }))
    : [];
  const presetKeys = heroRows.flatMap((r) => [
    heroBaseKey(r.id),
    ...r.walk.map((w) => w.key),
    ...r.stills.map((t) => t.key),
  ]);
  /** 기본 제공 항목인가(지우기를 안 붙인다). */
  const isBuiltinPart = (cat: PartCategory, id: string) =>
    (HERO_CATALOG[cat] as readonly string[]).includes(id);

  /** 늘어날 수 있는 카테고리 — **그림 한 장이 곧 항목**인 것들.
   *  몸·옷은 통짜 룩이 몸×옷 16장으로 고정이라 늘려도 그릴 그림이 없다. */
  const OPEN_PART_CATS: readonly PartCategory[] = ["hair", "acc"];

  // 기본 제공 + **운영자가 만든 것**(매니페스트가 목록이다) + 방금 만든 빈 칸.
  // "안 씀"(c0·a0)과 기본 옷(o0)은 뺀다 — 비어 있는 게 정답인 칸이라 채우면 오히려 안 나온다.
  const heroPartIds = (cat: PartCategory) => [
    ...allPartIds(cat, Object.keys(a)).filter((id) => !heroPartImageless(cat, id)),
    ...extraParts.filter((e) => e.cat === cat).map((e) => e.id),
  ];

  /** 새 자리 하나 — 안 쓰는 가장 작은 번호를 자동으로 잡는다(운영자가 번호를 고를 이유가 없다). */
  const addPart = (cat: PartCategory) =>
    setExtraParts((v) => [
      ...v,
      {
        cat,
        id: nextPartId(cat, [
          ...Object.keys(a),
          ...v.filter((e) => e.cat === cat).map((e) => heroPartKey(cat, e.id)),
        ]),
      },
    ]);

  /**
   * 자리 지우기 — 앞·옆·뒤 그림을 **한꺼번에** 지운다.
   * 기본 제공(c1~c4·a1~a4)은 지울 수 없다. 그걸 지우면 아이가 이미 고른 모자가 사라지는데,
   * 되돌리려면 그림을 다시 만들어야 한다 — 운영자가 만든 것과는 무게가 다르다.
   */
  const removePart = (cat: PartCategory, id: string) => {
    const keys = [heroPartKey(cat, id), `${heroPartKey(cat, id)}.side`, `${heroPartKey(cat, id)}.up`];
    const present = keys.filter((k) => a[k]?.url);
    const nick = a[heroPartKey(cat, id)]?.alt || id;
    const builtin = isBuiltinPart(cat, id);
    const msg = [
      `'${nick}' 을(를) 지울까요?`,
      "",
      present.length ? `지워지는 그림 ${present.length}장(앞·옆·뒤 중 있는 것)` : "아직 올린 그림이 없어요.",
      "",
      "이걸 쓰고 있던 아이는 다음에 들어올 때",
      "그 자리가 「안 씀」으로 보여요.",
      "",
      builtin
        ? "⚠️ 처음부터 들어 있던 것이에요." + "\n" + "되돌리려면 그림을 새로 만들어 올려야 해요."
        : "되돌리려면 그림을 다시 올리면 돼요.",
    ].join("\n");
    if (!window.confirm(msg)) return;
    keys.forEach((k) => setEntry(k, null));
    setExtraParts((v) => v.filter((e) => !(e.cat === cat && e.id === id)));
  };
  const heroKeys = slotByKey["hero.part"]
    ? PART_CATEGORIES.flatMap((cat) => heroPartIds(cat).map((id) => heroPartKey(cat, id)))
    : [];
  // UI 그림(버튼 아이콘·아래 메뉴·위 버튼·조이스틱) — 목록은 shared UI_ART 하나.
  // 웹이 그리는 것과 같은 정의라 "관리자에만 있는 슬롯"이 생기지 않는다.
  const uiKeys = slotByKey["ui.art"] ? uiArtKeys() : [];
  const worldKeys = WORLD_ZONES.flatMap((z) => WORLD_SLOT_KEYS.filter((k) => slotByKey[k]).map((k) => `${k}.${z}`));
  // 키는 지어내지 않는다 — 슬롯 정의가 가진 것을 쓴다(굿즈 배너는 `.default` 가 안 붙는다)
  const singleKeys = singleSlots.map(singleSlotKey);

  const landingKeys = landingItems.map((it) => `landing.char.${it.id}`);
  // 랜딩 캐릭터 앞뒤 순서(CSS zIndex). 랜딩은 Y정렬이 없어 z 자체가 곧 우선순위. 나의 히어로는 기본 층(0).
  const landingReorder = (key: string, op: ZOrderOp) => {
    const cur = a[key];
    if (!cur) return;
    const others = landingKeys.filter((k) => k !== key).map((k) => a[k]?.z ?? 0);
    const nz = Math.max(-50, Math.min(50, Math.round(reorderDepth(cur.z ?? 0, others, op, 1))));
    setEntry(key, { ...cur, z: nz === 0 ? undefined : nz });
  };
  const decorKeys = decorItems.map((it) => `decor.${it.id}`);
  type Cat = { key: string; label: string; icon: string; keys: string[] };
  const cats: Cat[] = [
    slotByKey["char.dir"] ? { key: "char", label: "캐릭터", icon: "🧍", keys: [...charKeys, ...presetKeys] } : null,
    { key: "world", label: "월드 아트", icon: "🗺️", keys: worldKeys },
    slotByKey["decor"] ? { key: "decor", label: "장식", icon: "🏠", keys: decorKeys } : null,
    slotByKey["landing.char"] ? { key: "main", label: "메인 화면", icon: "🏷️", keys: landingKeys } : null,
    slotByKey["hero.part"] ? { key: "hero", label: "히어로 파츠", icon: "🎨", keys: heroKeys } : null,
    slotByKey["ui.art"] ? { key: "ui", label: "UI 그림", icon: "🔘", keys: uiKeys } : null,
    { key: "etc", label: "기타 이미지", icon: "🖼️", keys: singleKeys },
    slotByKey["book.page"] ? { key: "book", label: "전자책", icon: "📖", keys: [] } : null,
  ].filter((c): c is Cat => c !== null);
  const activeCat = only ?? (cats.some((c) => c.key === cat) ? cat : (cats[0]?.key ?? ""));

  return (
    <>
      <div className="adminbar">
        <span className="adminbar__title">{only ? "메인 화면 그림" : "그림 전체 목록"}</span>
        <span className="adminbar__actions">
          <button type="button" className="btn" onClick={onExport} title="지금 상태를 파일로 저장해 보관해요">파일로 저장</button>
        </span>
      </div>
      {err ? <p className="console__err">{err}</p> : null}
      <div className={only ? "as-1col" : "as-2col"}>
        {/* 좌: 카테고리 네비 — 한 번에 한 분류만 보여 단순화. only 모드면 고를 게 없으니 숨긴다. */}
        {only ? null : (
        <aside className="as-nav">
          {cats.map((c) => {
            const [f, t] = countFilled(a, c.keys);
            return (
              <button key={c.key} type="button" className={`as-nav__item${activeCat === c.key ? " on" : ""}`} onClick={() => setCat(c.key)}>
                <span className="as-nav__ico" aria-hidden="true">{c.icon}</span>
                <span className="as-nav__label">{c.label}</span>
                {c.key !== "book" && c.key !== "ar" ? <span className="as-nav__count">{f}/{t}</span> : null}
              </button>
            );
          })}
        </aside>
        )}

        {/* 우: 선택한 분류의 슬롯만 */}
        <div className="as-body">
          {activeCat === "char" && slotByKey["char.dir"] ? (
            <section className="as-sec">
              <h2 className="as-sec__h">캐릭터 (앞·뒤·옆 + 걷기)</h2>
              <p className="console__hint">방향별 <b>가로 스트립</b>(걷기 프레임 왼→오, 정사각, 맨 왼쪽=대기). 옆은 왼쪽에 자동 반전. NPC는 [🗺️ 체험공간 배치]에서 <code>npc_sprite</code> 값(괄호 안 id)으로 배치.</p>
              {characters.map((c) => {
                const keys = CHAR_DIRS.map((d) => `${c.key}.${d.d}`);
                return (
                  <Fold key={c.key} title={c.label} id={c.custom ? c.id : undefined} thumb={a[`${c.key}.down`]?.url} keys={keys} assets={a}>
                    <div className="grid">
                      {CHAR_DIRS.map(({ d, label }) => {
                        const key = `${c.key}.${d}`;
                        return <SlotUploader key={key} slotKey={key} label={label} spec={slotByKey["char.dir"]!.spec} entry={a[key]} onChange={setEntry} alt={d === "down" ? c.label : undefined} procedural="코드로 그린 기본 캐릭터가 보여요" />;
                      })}
                    </div>
                    {/* 🔴 지우기는 **펼친 안쪽**에 둔다 — 여는 줄에 두면 누를 때 접힘이 같이 열리고, 실수로 누르기도 쉽다. */}
                    {c.custom ? (
                      <button type="button" className="acc__rm" onClick={() => removeItem(keys, { id: c.id, label: c.label }, setExtraNpc)}>✕ 제거</button>
                    ) : null}
                  </Fold>
                );
              })}
              <AddBar placeholder="새 NPC 이름(한글 가능, 예: 토끼)" onAdd={(t) => addItem(t, setExtraNpc)} />

              {showPresets ? (
                <>
                  <h2 className="as-sec__h" style={{ marginTop: 28 }}>
                    아이가 고르는 캐릭터 <em className="as-sec__count">{heroRows.length}명</em>
                  </h2>
                  <p className="console__hint">
                    처음부터 들어 있던 것과 <b>[➕ 직접 만들기]로 만드신 것을 모두</b> 보여줘요.
                    <b>기본 그림</b>만 넣어도 아이 화면에 나오고, 걷기 스트립을 넣으면 마을에서 걸어요
                    (<code>char.dir</code> 규격 · 가로 정사각 · 맨 왼쪽=대기).
                  </p>
                  {heroRows.map((r) => {
                    const baseKey = heroBaseKey(r.id);
                    return (
                      <Fold
                        key={baseKey}
                        title={r.label}
                        id={r.id}
                        thumb={a[baseKey]?.url}
                        keys={[baseKey, ...r.stills.map((t) => t.key), ...r.walk.map((w) => w.key)]}
                        assets={a}
                      >
                        <div className="grid">
                          <SlotUploader
                            slotKey={baseKey}
                            label="기본 그림(정지)"
                            spec={slotByKey["hero.base"]!.spec}
                            entry={a[baseKey]}
                            onChange={setEntry}
                            alt={r.label}
                          />
                          {/* 걷는 그림(앞·옆·뒷모습) — 걷기를 굽는 바탕이다. [➕ 직접 만들기]에서
                              넣는 것과 **같은 자리**라, 여기 없으면 「전체 목록」이 전체가 아니다. */}
                          {r.stills.map((t) => (
                            <SlotUploader
                              key={t.key}
                              slotKey={t.key}
                              label={t.label}
                              spec={slotByKey["hero.base"]!.spec}
                              entry={a[t.key]}
                              onChange={setEntry}
                            />
                          ))}
                          {r.walk.map((w) => (
                            <SlotUploader
                              key={w.key}
                              slotKey={w.key}
                              label={w.label}
                              spec={slotByKey["char.dir"]!.spec}
                              entry={a[w.key]}
                              onChange={setEntry}
                            />
                          ))}
                        </div>
                      </Fold>
                    );
                  })}
                </>
              ) : null}
            </section>
          ) : null}

          {activeCat === "world" ? (
            <section className="as-sec">
              <h2 className="as-sec__h">월드 아트 (존별 배경·바닥·벽)</h2>
              <p className="console__hint">존별 배경 일러스트(있으면 바닥 대체)·바닥/벽 타일(48px).</p>
              {WORLD_ZONES.map((z, i) => {
                const ks = WORLD_SLOT_KEYS.filter((k) => slotByKey[k]);
                return (
                  <Fold key={z} title={z} keys={ks.map((k) => `${k}.${z}`)} assets={a} thumb={a[`world.background.${z}`]?.url} open={i === 0}>
                    <div className="grid">
                      {ks.map((k) => {
                        const key = `${k}.${z}`;
                        return <SlotUploader key={key} slotKey={key} label={slotByKey[k]!.label} spec={slotByKey[k]!.spec} entry={a[key]} onChange={setEntry} procedural="코드로 그린 기본 배경·바닥·벽이 보여요" />;
                      })}
                    </div>
                  </Fold>
                );
              })}
            </section>
          ) : null}

          {activeCat === "decor" && slotByKey["decor"] ? (
            <section className="as-sec">
              <h2 className="as-sec__h">장식 (건물·소품)</h2>
              <p className="console__hint">투명 배경 스프라이트. [🗺️ 체험공간 배치]에서 <code>decor</code> 오브젝트의 <code>decor_sprite</code> 값(괄호 안 id)으로 배치돼요.</p>
              <div className="grid">
                {decorItems.map((it) => {
                  const key = `decor.${it.id}`;
                  const custom = !DECOR_PRESET.some((d) => d.id === it.id);
                  return <SlotUploader key={key} slotKey={key} label={custom ? `${it.label} (${it.id})` : it.label} spec={slotByKey["decor"]!.spec} entry={a[key]} onChange={setEntry} alt={it.label} procedural="코드로 그린 기본 장식이 보여요" onRemove={custom ? () => removeItem([key], it, setExtraDecor) : undefined} />;
                })}
              </div>
              <AddBar placeholder="새 장식 이름(한글 가능, 예: 다리)" onAdd={(t) => addItem(t, setExtraDecor)} />
            </section>
          ) : null}

          {activeCat === "main" && slotByKey["landing.char"] ? (
            <section className="as-sec">
              <h2 className="as-sec__h">메인 화면 캐릭터</h2>
              <p className="console__hint">
                메인(랜딩) 화면에 서는 캐릭터예요. 캐릭터마다 <b>① 그림 한 장</b>을 올리고, 움직이게 하고 싶으면 <b>② 움직임</b>에 그림을 여러 장 올리세요.
                다 하면 위의 <b>[🚀 게시]</b>를 눌러야 웹에 나와요. 위치는 미리보기에서 드래그로 옮길 수 있어요.
              </p>
              <div className="grid">
                {landingItems.map((it) => {
                  const key = `landing.char.${it.id}`;
                  const stripKey = `${key}.strip`;
                  const custom = !LANDING_CHARS.some((c) => c.id === it.id);
                  const entry = a[key];
                  const z = entry?.z ?? 0;
                  return (
                    <div key={key} className="land-item">
                      <div className="land-item__step">① 그림</div>
                      <SlotUploader slotKey={key} label={it.label} spec={slotByKey["landing.char"]!.spec} entry={entry} onChange={setEntry} alt={it.label} onRemove={custom ? () => removeItem([key, stripKey], it, setExtraLanding) : undefined} />
                      {/* ② 움직임(애니) — 같은 카드에서 그림 여러 장 → 자동 조립. 정지 그림이 있어야 의미 있음. */}
                      {entry ? (
                        <>
                          <div className="land-item__step">② 움직임 <em>(선택)</em></div>
                          <StripBuilder slotKey={stripKey} entry={a[stripKey]} onChange={setEntry} />
                          <div className="objz objz--compact">
                            <span className="objz__label">앞뒤 순서 {z !== 0 ? <em className="objz__val">(z {z > 0 ? "+" : ""}{z})</em> : <em className="objz__val objz__val--auto">(기본)</em>}</span>
                            <div className="objz__btns">
                              <button type="button" onClick={() => landingReorder(key, "front")} title="맨 앞으로">⤒ 맨 앞</button>
                              <button type="button" onClick={() => landingReorder(key, "forward")} title="한 단계 앞으로">↑ 앞으로</button>
                              <button type="button" onClick={() => landingReorder(key, "backward")} title="한 단계 뒤로">↓ 뒤로</button>
                              <button type="button" onClick={() => landingReorder(key, "back")} title="맨 뒤로">⤓ 맨 뒤</button>
                            </div>
                            {z !== 0 ? (
                              <button type="button" className="objz__reset" onClick={() => setEntry(key, { ...entry, z: undefined })}>순서 기본(초기화)</button>
                            ) : null}
                          </div>
                        </>
                      ) : (
                        <p className="land-item__tip">먼저 ① 그림을 올리면 움직임을 넣을 수 있어요.</p>
                      )}
                    </div>
                  );
                })}
              </div>
              <AddBar placeholder="새 캐릭터 이름(한글 가능)" onAdd={(t) => addItem(t, setExtraLanding)} />
            </section>
          ) : null}

          {activeCat === "ui" && slotByKey["ui.art"] ? (
            <section className="as-sec">
              <h2 className="as-sec__h">UI 그림 (버튼·메뉴)</h2>
              <p className="console__hint">
                <b>작은 정사각 투명 그림</b>(긴 변 256px 이하). 안 올리면 <b>번들 기본 그림</b>이,
                그것도 없으면 <b>이모지</b>가 나와요 — 어느 쪽이든 화면은 안 깨져요.
                지금 <b>동작 아이콘 12종은 그림이 없어 이모지로</b> 나오고 있어요.
              </p>
              {(Object.keys(UI_ART_GROUP_LABEL) as UiArtGroup[]).map((g, i) => {
                const rows = UI_ART.filter((x) => x.group === g);
                if (!rows.length) return null;
                return (
                  <Fold
                    key={g}
                    title={UI_ART_GROUP_LABEL[g]}
                    keys={rows.map((x) => uiArtKey(x.group, x.name))}
                    assets={a}
                    open={i === 0}
                  >
                    <div className="grid">
                      {rows.map((x) => {
                        const key = uiArtKey(x.group, x.name);
                        return (
                          <SlotUploader
                            key={key}
                            slotKey={key}
                            label={`${x.emoji} ${x.label}`}
                            spec={slotByKey["ui.art"]!.spec}
                            entry={a[key]}
                            onChange={setEntry}
                            procedural={x.bundled ? "번들 기본 그림이 보여요" : "그림이 없어 이모지가 보여요"}
                          />
                        );
                      })}
                    </div>
                  </Fold>
                );
              })}
            </section>
          ) : null}

          {activeCat === "hero" && slotByKey["hero.part"] ? (
            <section className="as-sec">
              <h2 className="as-sec__h">히어로 파츠 (커스터마이즈)</h2>
              <p className="console__hint">
                <b>정사각 투명 그림</b> — 아이 전신이 프레임을 거의 채우고 발끝이 아래에 닿게.
                모자·소품은 그 아이 위에 얹힐 <b>제자리</b>(머리 위·가슴 옆)에 그려 주세요. 히어로 만들기에서 선택지로 노출됩니다.
              </p>
              <p className="console__hint">
                모자·소품은 <b>몇 개든</b> 만들 수 있어요 — [＋ 새로 만들기]로 칸을 하나 늘리고 그림을 올리면
                그때부터 아이 화면 선택지에 나와요. 아이(몸)와 옷은 <b>정해진 조합</b>이라 늘릴 수 없어요.
              </p>
              {PART_CATEGORIES.map((pc, i) => (
                <Fold
                  key={pc}
                  title={HERO_CAT_LABEL[pc] ?? pc}
                  keys={heroPartIds(pc).map((id) => heroPartKey(pc, id))}
                  assets={a}
                  open={i === 0}
                >
                  {/* 🔴 [＋ 새로 만들기]는 **펼친 안쪽**에 둔다 — 여는 줄에 두면 누를 때 접힘이 같이 열린다.
                      모자·소품은 몇 개든 만들 수 있다(매니페스트가 목록이라 자리를 뚫어 둘 필요가 없다).
                      몸·옷은 통짜 룩 그림이 몸×옷 16장으로 고정이라 늘릴 수 없다(그릴 그림이 없다). */}
                  {OPEN_PART_CATS.includes(pc) ? (
                    <p className="hint">
                      <button type="button" className="btn btn--sm" onClick={() => addPart(pc)}>
                        ＋ 새로 만들기
                      </button>
                    </p>
                  ) : null}
                  <div className="grid">
                    {heroPartIds(pc).flatMap((id) => {
                      const key = heroPartKey(pc, id);
                      // 모자·소품은 **방향마다 다르게 보인다**(옆에서 본 챙은 타원, 뒤에서 리본은 안 보임).
                      // 웹은 `<키>.side`·`<키>.up` 이 있으면 쓰고 없으면 앞모습으로 폴백한다(overlayDirKeys) —
                      // 그래서 앞모습 한 장만 올려도 오늘과 똑같이 동작하고, 나중에 올리면 그때부터 쓰인다.
                      //
                      // 문제는 **운영자가 그걸 알 수 없었다는 것**이다. 빈 칸이 셋이면 "덜 만든 것"으로 보이는데,
                      // 비니처럼 돌려도 같은 모자는 앞 그림 한 장이 정답이고, 가슴 배지의 뒷모습은 아예 안
                      // 그리는 게 정답이다. 그래서 **지금 무엇이 쓰이는지**를 칸마다 적는다(판정은 shared 하나).
                      if (!LOOK_OVERLAY_CATEGORIES.includes(pc as OverlayCategory)) {
                        return [
                          <SlotUploader
                            key={key}
                            slotKey={key}
                            label={id}
                            spec={slotByKey["hero.part"]!.spec}
                            entry={a[key]}
                            onChange={setEntry}
                          />,
                        ];
                      }
                      return overlayDirReport(key, (k) => !!a[k])
                        // 웹이 그 방향에서 **아예 안 그리는** 것(가슴 배지의 뒷모습)은 올릴 자리를 만들지
                        // 않는다 — 만들면 "올렸는데 왜 안 나오지"가 된다.
                        .filter((r) => r.state !== "hidden")
                        .map((r) => {
                          // **웹이 그 방향에서 첫 번째로 찾는 키** 그대로 올린다 —
                          // 접미사 규약(`.side`·`.up`)을 관리자에 또 적으면 언젠가 갈린다.
                          const slot = overlayDirKeys(key, r.dir)[0]!;
                          return (
                            <SlotUploader
                              key={slot}
                              slotKey={slot}
                              label={`${id} · ${DIR_LABEL[r.dir]}`}
                              spec={slotByKey["hero.part"]!.spec}
                              entry={a[slot]}
                              onChange={setEntry}
                              procedural={
                                r.state === "fallback" ? "지금은 앞 그림을 그대로 써요" : undefined
                              }
                              // 지우기는 **앞 칸에만** 붙인다 — 방향 칸에 붙이면 "그 방향만 지워지나?"가 된다.
                              // 기본 제공(c1~c4·a1~a4)에는 안 붙인다: 다시 만들려면 그림부터 그려야 한다.
                              onRemove={r.dir === "down" ? () => removePart(pc, id) : undefined}
                            />
                          );
                        });
                    })}
                  </div>
                </Fold>
              ))}
            </section>
          ) : null}

          {activeCat === "etc" ? (
            <section className="as-sec">
              <h2 className="as-sec__h">기타 이미지 (이벤트·추천·랜딩 배경·AR 등)</h2>
              <div className="grid">
                {singleSlots.map((s) => {
                  const key = singleSlotKey(s);
                  return <SlotUploader key={key} slotKey={key} label={`${s.group} · ${s.label}`} spec={s.spec} entry={a[key]} onChange={setEntry} />;
                })}
              </div>
            </section>
          ) : null}

          {activeCat === "book" && slotByKey["book.page"] ? (
            <section className="as-sec">
              <h2 className="as-sec__h">전자책</h2>
              <p className="console__hint">전자책(여러 권)은 상단 <b>전자책 만들기</b> 탭에서 제목·페이지 추가/삭제·그림·동영상을 편집하고 저장/삭제하세요.</p>
              <p className="zone__tip">
                전자책 페이지 그림·동영상 업로드가 <b>전자책 만들기</b> 탭으로 옮겨졌어요.{" "}
                {/* 🔴 못 읽은 것을 「0쪽」이라고 말하지 않는다 — 「0」은 「안 셌다」일 수 있다 */}
                {pages === null
                  ? "(정적 기본 책은 지금 못 읽었어요)"
                  : `(정적 기본 책 ${pages.length}쪽)`}
              </p>
            </section>
          ) : null}
        </div>
      </div>
    </>
  );
}
